import { useTranslation } from '@e-kyc/translations';
import { useController } from 'react-hook-form';

import { Form } from '../../../../../../components';
import { ID_CARD_NUMBER_MASK, LASER_NUMBER_MASK } from '../../utils';
import { ExpirationDateInputGroup } from '../expiration-date-input-group';
import { IdCardConfirmationAlert } from '../id-card-confirmation-alert';

export interface IdCardConfirmationFormProps {
  data: {
    titleOptions: Form.FormSelectOption[];
    dayOptions: Form.FormSelectOption[];
    monthOptions: Form.FormSelectOption[];
    yearOptions: Form.FormSelectOption[];
    expiryDateOptions: Form.FormSelectOption[];
    expiryMonthOptions: Form.FormSelectOption[];
    expiryYearOptions: Form.FormSelectOption[];
    customRenderMonthDisplayValue: (value: Form.FormSelectOption['value']) => string;
  };
}

export function IdCardConfirmationForm(props: Readonly<IdCardConfirmationFormProps>) {
  const {
    data: {
      titleOptions,
      dayOptions,
      monthOptions,
      yearOptions,
      expiryDateOptions,
      expiryMonthOptions,
      expiryYearOptions,
      customRenderMonthDisplayValue,
    },
  } = props;
  const { t } = useTranslation();
  const {
    fieldState: { error: dateOfBirthError },
  } = useController({
    name: 'dateOfBirth',
  });

  return (
    <form className='flex flex-1 flex-col gap-4 p-4 sm:mt-24'>
      <h4 aria-label={t('idCardDetails.form.header')}>{t('idCardDetails.form.header')}</h4>
      <Form.Input
        name='idCardNumber'
        label={t('idCardDetails.form.id.label')}
        maskOptions={{
          mask: ID_CARD_NUMBER_MASK,
        }}
        disabled
      />
      <Form.Select
        name='title'
        label={t('idCardDetails.form.title.label')}
        placeholder={t('idCardDetails.form.placeholder.pleaseSelect', {
          label: t('idCardDetails.form.title.label'),
        })}
        options={titleOptions}
        required
      />
      <Form.Input
        name='firstName'
        label={t('idCardDetails.form.firstName.label')}
        placeholder={t('idCardDetails.form.placeholder.pleaseFill', {
          label: t('idCardDetails.form.firstName.label'),
        })}
        required
      />
      <Form.Input
        name='middleName'
        label={t('idCardDetails.form.middleName.label')}
        placeholder={t('idCardDetails.form.placeholder.pleaseFill', {
          label: t('idCardDetails.form.middleName.label'),
        })}
        helperText={t('idCardDetails.form.middleName.helperText')}
      />
      <Form.Input
        name='lastName'
        label={t('idCardDetails.form.lastName.label')}
        placeholder={t('idCardDetails.form.placeholder.pleaseFill', {
          label: t('idCardDetails.form.lastName.label'),
        })}
        required
      />
      <div>
        <p className='after:text-danger mb-2 text-start text-sm font-semibold after:ml-1 after:content-["*"]'>
          {t('idCardDetails.form.dateOfBirth.label')}
        </p>
        <div className='relative grid w-full grid-cols-3 gap-2'>
          <Form.Select
            name='dateOfBirth.date'
            placeholder={t('idCardDetails.form.placeholder.day')}
            options={dayOptions}
            classNameOverrides={{
              optionWrapper: 'mx-4 w-[calc(100%-32px)] [--anchor-max-height:160px] left-0!',
            }}
            inputMode='numeric'
          />
          <Form.Select
            name='dateOfBirth.month'
            placeholder={t('idCardDetails.form.placeholder.month')}
            options={monthOptions}
            classNameOverrides={{
              optionWrapper: 'mx-4 w-[calc(100%-32px)] [--anchor-max-height:160px] left-0!',
            }}
            renderDisplayValue={customRenderMonthDisplayValue}
            disableInput
          />
          <Form.Select
            name='dateOfBirth.year'
            placeholder={t('idCardDetails.form.placeholder.year')}
            options={yearOptions}
            classNameOverrides={{
              optionWrapper: 'mx-4 w-[calc(100%-32px)] [--anchor-max-height:160px] left-0!',
            }}
            inputMode='numeric'
          />
        </div>
        <p className='text-danger -mt-1 text-sm'>{dateOfBirthError?.root?.message}</p>
      </div>
      <ExpirationDateInputGroup
        dayOptions={expiryDateOptions}
        monthOptions={expiryMonthOptions}
        expiryYearOptions={expiryYearOptions}
        customRenderMonthDisplayValue={customRenderMonthDisplayValue}
      />
      <Form.Input
        name='laserNumber'
        label={t('idCardDetails.form.laserNo.label')}
        placeholder={t('idCardDetails.form.placeholder.pleaseFill', {
          label: t('idCardDetails.form.laserNo.label'),
        })}
        maskOptions={{
          mask: LASER_NUMBER_MASK,
          prepare: (str: string) => str.toUpperCase(),
        }}
        required
      />
      <IdCardConfirmationAlert />
    </form>
  );
}
